import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSessionTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  let rel;
  if (diffMin < 1) rel = 'şimdi';
  else if (diffMin < 60) rel = `${diffMin} dk önce`;
  else if (diffMin < 1440) rel = `${Math.floor(diffMin / 60)} sa önce`;
  else rel = `${Math.floor(diffMin / 1440)} gün önce`;
  return `${d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} (${rel})`;
};

const fmtDevice = (ua) => {
  if (!ua) return '—';
  const mobil = /Mobile|Android|iPhone|iPad/i.test(ua) ? 'Mobil' : 'Masaüstü';
  let browser = 'Tarayıcı';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome\//i.test(ua)) browser = 'Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/Safari\//i.test(ua)) browser = 'Safari';
  return `${mobil} · ${browser}`;
};

export const SistemPanel = () => {
  const [sessionTimeout, setSessionTimeout] = useState(60);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sessions`, { withCredentials: true });
      setSessions(res.data.sessions || []);
    } catch (e) { flash('❌ Oturumlar yüklenemedi: ' + (e.response?.data?.detail || e.message)); }
    finally { setSessionsLoading(false); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/settings/session-config`, { withCredentials: true })
      .then(res => setSessionTimeout(res.data.idle_timeout_minutes || 60))
      .catch(() => {});
    loadSessions();
    loadBackups();
  }, [loadSessions]);

  const loadBackups = async () => {
    try {
      const res = await axios.get(`${API}/admin/backups`, { withCredentials: true });
      setBackups(res.data.backups || []);
    } catch (e) { /* sessiz */ }
  };

  const simdiYedekAl = async () => {
    setBackupBusy(true);
    try {
      const res = await axios.post(`${API}/admin/backups`, {}, { withCredentials: true });
      flash(`✅ Yedek alındı: ${res.data.filename} (${res.data.collections} koleksiyon)`);
      loadBackups();
    } catch (e) { flash('❌ Yedek alınamadı: ' + (e.response?.data?.detail || e.message)); }
    finally { setBackupBusy(false); }
  };

  const indirBackup = async (filename) => {
    await download(`admin/backups/${filename}`, filename, 'Yedek indirme');
  };

  const fmtBoyut = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  const saveTimeout = async () => {
    try {
      await axios.put(`${API}/settings/session-config`, { idle_timeout_minutes: Number(sessionTimeout) }, { withCredentials: true });
      flash('✅ Oturum zaman aşımı ayarı kaydedildi');
    } catch (e) { flash('❌ Hata: ' + (e.response?.data?.detail || e.message)); }
  };

  const download = async (path, filename, errLabel) => {
    try {
      const response = await axios.get(`${API}/${path}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { flash(`❌ ${errLabel} hatası: ` + (e.response?.data?.detail || e.message)); }
  };

  const terminateSession = async (sid) => {
    if (!window.confirm('Bu oturumu sonlandırmak istediğinize emin misiniz? Kullanıcı tekrar giriş yapmak zorunda kalacak.')) return;
    try {
      await axios.delete(`${API}/admin/sessions/${sid}`, { withCredentials: true });
      loadSessions();
    } catch (e) { flash('❌ Hata: ' + (e.response?.data?.detail || e.message)); }
  };

  const terminateOthers = async () => {
    if (!window.confirm('Sizin oturumunuz HARİÇ tüm oturumlar sonlandırılacak. Emin misiniz?')) return;
    try {
      const res = await axios.delete(`${API}/admin/sessions`, { withCredentials: true });
      flash(`✅ ${res.data.terminated} oturum sonlandırıldı`);
      loadSessions();
    } catch (e) { flash('❌ Hata: ' + (e.response?.data?.detail || e.message)); }
  };

  const stamp = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6" data-testid="sistem-panel">
      {msg && <div className="p-3 bg-gray-800 border border-cyan-700/50 rounded-lg text-sm text-white" data-testid="sistem-panel-msg">{msg}</div>}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">💾 Database Yedek</CardTitle>
            <CardDescription className="text-gray-500 text-xs">Tüm veritabanını bilgisayarınıza indirin (denetim kaydına işlenir)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <button onClick={() => download('export/all-json', `veritabani-yedek-${stamp}.json`, 'JSON export')}
              className="w-full text-left p-4 bg-gray-800/60 hover:bg-gray-800 border border-gray-700 rounded-lg transition-colors" data-testid="db-backup-json-btn">
              <div className="font-medium text-sm text-white">Tek Dosya (JSON)</div>
              <div className="text-xs text-gray-500">Tüm veriler tek JSON dosyasında</div>
            </button>
            <button onClick={() => download('export/collections-zip', `mongodb-collections-${stamp}.zip`, 'ZIP export')}
              className="w-full text-left p-4 bg-gray-800/60 hover:bg-gray-800 border border-gray-700 rounded-lg transition-colors" data-testid="db-backup-zip-btn">
              <div className="font-medium text-sm text-white">Collection Bazlı (ZIP)</div>
              <div className="text-xs text-gray-500">Her collection ayrı JSON — Studio 3T / Compass ile içe aktarım için</div>
            </button>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">⏱ Oturum Zaman Aşımı</CardTitle>
            <CardDescription className="text-gray-500 text-xs">Kullanıcılar belirtilen süre boyunca işlem yapmazsa otomatik çıkış yapılır</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label className="text-gray-300 text-xs">Hareketsizlik Süresi (dakika)</Label>
              <Input type="number" min={1} max={1440} value={sessionTimeout} onChange={(e) => setSessionTimeout(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white h-9" data-testid="session-timeout-input" />
              <p className="text-xs text-gray-500">Süre dolmadan 1 dakika önce kullanıcıya geri sayımlı uyarı gösterilir. Geçerli aralık: 1 - 1440 dakika.</p>
            </div>
            <Button onClick={saveTimeout} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="session-settings-save">Kaydet</Button>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-white text-lg">🗄 Otomatik Yedekler ({backups.length}/7)</CardTitle>
              <CardDescription className="text-gray-500 text-xs">
                Her gece 03:00'te (TR) otomatik yedek alınır ve sunucuda saklanır. En fazla 7 yedek tutulur; 7'ye ulaşınca yeni yedek gelmeden önce en eskisi silinir.
              </CardDescription>
            </div>
            <Button size="sm" onClick={simdiYedekAl} disabled={backupBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid="backup-now-btn">
              {backupBusy ? '⏳ Yedekleniyor…' : '🔄 Şimdi Yedek Al'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">Henüz yedek yok. "Şimdi Yedek Al" ile ilk yedeğinizi oluşturabilirsiniz.</p>
          ) : (
            <div className="divide-y divide-gray-800">
              {backups.map((b) => (
                <div key={b.filename} className="flex items-center justify-between py-2 gap-2" data-testid={`backup-row-${b.filename}`}>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-mono truncate">{b.filename}</p>
                    <p className="text-gray-500 text-xs">{b.created_at ? new Date(b.created_at).toLocaleString('tr-TR') : '—'} · {fmtBoyut(b.size)}</p>
                  </div>
                  <Button size="sm" onClick={() => indirBackup(b.filename)} variant="outline" className="border-gray-700 bg-transparent text-gray-300 hover:text-white h-7 text-xs flex-shrink-0" data-testid={`backup-download-${b.filename}`}>⬇ İndir</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-white text-lg">👥 Aktif Oturumlar ({sessions.length})</CardTitle>
              <CardDescription className="text-gray-500 text-xs">Giriş yapmış tüm kullanıcı oturumları — şüpheli oturumları tek tıkla sonlandırın</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={terminateOthers} variant="outline" className="border-red-800 bg-transparent text-red-400 hover:bg-red-900/20 h-8 text-xs" data-testid="terminate-others-btn">Diğer Tüm Oturumları Sonlandır</Button>
              <Button size="sm" onClick={loadSessions} variant="outline" className="border-gray-700 bg-transparent text-gray-300 hover:text-white h-8 text-xs" data-testid="refresh-sessions-btn">Yenile</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {sessionsLoading ? (
            <div className="text-gray-400 text-center py-8">Yükleniyor...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="active-sessions-table">
                <thead>
                  <tr className="border-b border-gray-700 text-xs text-gray-400 bg-gray-800/50">
                    <th className="text-left py-2 px-3">Kullanıcı</th>
                    <th className="text-left py-2 px-3">Giriş</th>
                    <th className="text-left py-2 px-3">Son Aktivite</th>
                    <th className="text-left py-2 px-3">IP</th>
                    <th className="text-left py-2 px-3">Cihaz</th>
                    <th className="text-center py-2 px-3">Durum</th>
                    <th className="text-center py-2 px-3">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-6 text-gray-500">Aktif oturum yok</td></tr>
                  ) : sessions.map(s => (
                    <tr key={s.id} className={`border-b border-gray-800/50 ${s.is_current ? 'bg-cyan-900/15' : 'hover:bg-gray-800/30'}`} data-testid={`session-row-${s.id}`}>
                      <td className="py-2 px-3 text-white">
                        <div className="flex items-center gap-2">
                          <span>{s.name || s.username}</span>
                          {s.is_admin && <Badge className="bg-amber-700 text-[9px]">Admin</Badge>}
                          {s.is_current && <Badge className="bg-cyan-700 text-[9px]">Bu oturum</Badge>}
                        </div>
                        <span className="text-[11px] text-gray-500">@{s.username}</span>
                      </td>
                      <td className="py-2 px-3 text-gray-400 text-xs">{fmtSessionTime(s.created_at)}</td>
                      <td className="py-2 px-3 text-gray-300 text-xs">{fmtSessionTime(s.last_activity)}</td>
                      <td className="py-2 px-3 text-gray-400 text-xs">{s.ip || '—'}</td>
                      <td className="py-2 px-3 text-gray-400 text-xs">{fmtDevice(s.user_agent)}</td>
                      <td className="py-2 px-3 text-center">
                        <Badge className={`text-[10px] ${s.aktif ? 'bg-green-700' : 'bg-gray-700'}`}>{s.aktif ? 'Aktif' : 'Boşta'}</Badge>
                      </td>
                      <td className="py-2 px-3 text-center">
                        {s.is_current ? (
                          <span className="text-[10px] text-gray-600">—</span>
                        ) : (
                          <Button size="sm" onClick={() => terminateSession(s.id)} className="bg-red-700 hover:bg-red-600 h-7 text-xs" data-testid={`terminate-session-${s.id}`}>Sonlandır</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
